import React, { useState, useEffect, useRef, useCallback } from 'react';

// ============================================
// DARIJA BUDDY BOT - Simple Tip Bot with Transport Search
// ============================================

const DarijaBuddyBot = ({ API, userToken, hasPaidPlan, currentMood, currentPage, isDarkMode, showNotification }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [buddyConfig, setBuddyConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [isSilenced, setIsSilenced] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  // Transport search state
  const [transportMode, setTransportMode] = useState(false);
  const [transportCities, setTransportCities] = useState([]);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState('');
  const [transportResult, setTransportResult] = useState(null);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [currencyRates, setCurrencyRates] = useState({ MAD_to_EUR: 0.093, MAD_to_USD: 0.10 });
  
  const lastInteractionRef = useRef(Date.now());
  const periodicTimerRef = useRef(null);
  const reappearTimerRef = useRef(null);
  const userId = userToken ? 'user_' + userToken.substring(0, 8) : null;

  // Fetch currency rates on mount
  useEffect(() => {
    fetchCurrencyRates();
  }, []);

  const fetchCurrencyRates = async () => {
    try {
      const res = await fetch(`${API}/buddy/transport/currency`);
      if (res.ok) {
        const data = await res.json();
        setCurrencyRates(data);
      }
    } catch (err) {
      console.error('Failed to fetch currency rates:', err);
    }
  };

  // Convert MAD to EUR/USD
  const convertPrice = (madPrice) => {
    const eur = Math.round(madPrice * currencyRates.MAD_to_EUR);
    const usd = Math.round(madPrice * currencyRates.MAD_to_USD);
    return { eur, usd };
  };

  // Format frequency for display
  const formatFrequency = (perDay) => {
    if (!perDay || perDay === 0) return 'On request';
    if (perDay >= 30) return 'Every 30 min';
    if (perDay >= 20) return 'Every 45 min';
    if (perDay >= 12) return 'Every hour';
    if (perDay >= 8) return 'Every 1.5h';
    if (perDay >= 6) return 'Every 2h';
    if (perDay >= 4) return `${perDay}x daily`;
    if (perDay >= 2) return `${perDay}x daily`;
    return 'Limited service';
  };

  useEffect(() => {
    fetchBuddyConfig();
    return () => {
      if (periodicTimerRef.current) clearInterval(periodicTimerRef.current);
      if (reappearTimerRef.current) clearTimeout(reappearTimerRef.current);
    };
  }, []);

  const fetchBuddyConfig = async () => {
    try {
      const res = await fetch(`${API}/buddy/config`);
      if (res.ok) {
        const data = await res.json();
        setBuddyConfig(data);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch buddy config:', err);
      setIsLoading(false);
    }
  };

  // Fetch transport cities
  const fetchTransportCities = async () => {
    try {
      const res = await fetch(`${API}/buddy/transport/cities`);
      if (res.ok) {
        const data = await res.json();
        setTransportCities(data.cities || []);
      }
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  // Start transport search mode
  const startTransportMode = () => {
    fetchTransportCities();
    setTransportMode(true);
    setTransportResult(null);
    setSelectedTransport(null);
    setFromCity('');
    setToCity('');
    setCurrentMessage({
      text: "🚆 Where are you traveling?",
      responses: []
    });
    setIsVisible(true);
    setIsTyping(false);
  };

  // Search transport route
  const searchTransport = async () => {
    if (!fromCity || !toCity) return;
    
    setIsTyping(true);
    try {
      const res = await fetch(`${API}/buddy/transport/search?from_city=${encodeURIComponent(fromCity)}&to_city=${encodeURIComponent(toCity)}`);
      if (res.ok) {
        const data = await res.json();
        setIsTyping(false);
        
        if (data.found) {
          setTransportResult(data);
          const best = data.transports.find(t => t.is_best) || data.transports[0];
          setSelectedTransport(best);
          const distText = data.distance_km ? `\n📏 ${data.distance_km} km (${Math.round(data.distance_km * 0.621)} mi)` : '';
          setCurrentMessage({
            text: `📍 ${fromCity} → ${toCity}${distText}\nHere are your options:`,
            responses: []
          });
        } else {
          let msg = `😕 No direct route for ${fromCity} → ${toCity}`;
          if (data.suggestion) {
            msg += `\n\n💡 Try: ${data.suggestion}`;
          }
          setCurrentMessage({
            text: msg,
            responses: [{ text: "Try another route 🔄" }]
          });
          setTransportResult(null);
        }
      }
    } catch (err) {
      console.error('Transport search failed:', err);
      setIsTyping(false);
      setCurrentMessage({
        text: "😕 Couldn't search routes. Please try again.",
        responses: []
      });
    }
  };

  // Reset transport mode
  const resetTransport = () => {
    setTransportMode(false);
    setTransportResult(null);
    setSelectedTransport(null);
    setFromCity('');
    setToCity('');
  };

  const startBuddyConversation = useCallback(async () => {
    if (isSilenced) return;
    
    if (reappearTimerRef.current) {
      clearTimeout(reappearTimerRef.current);
      reappearTimerRef.current = null;
    }
    
    try {
      let url = `${API}/buddy/start`;
      if (userId) url += `?userId=${userId}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.show) {
          setCurrentStep(data.step);
          window.__buddyContext = {
            conversationId: data.conversationId,
            nodeId: data.nodeId
          };
          
          setIsVisible(true);
          setIsExiting(false);
          setIsTyping(true);
          
          setTimeout(() => {
            setIsTyping(false);
            setCurrentMessage({
              text: data.message,
              imageUrl: data.imageUrl,
              responses: data.responses
            });
          }, 500);
          
          if (data.settings) {
            setBuddyConfig(prev => ({ ...prev, ...data.settings }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to start buddy conversation:', err);
    }
  }, [API, userId, isSilenced]);

  const handleResponse = async (response) => {
    lastInteractionRef.current = Date.now();
    setIsTyping(true);
    
    try {
      const formData = new FormData();
      if (userId) formData.append('userId', userId);
      formData.append('currentStep', currentStep);
      formData.append('responseText', response.text);
      
      const ctx = window.__buddyContext || {};
      if (ctx.conversationId) formData.append('conversationId', ctx.conversationId);
      if (ctx.nodeId) formData.append('currentNodeId', ctx.nodeId);
      
      const res = await fetch(`${API}/buddy/respond`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.conversationId && data.nodeId) {
          window.__buddyContext = {
            conversationId: data.conversationId,
            nodeId: data.nodeId
          };
        }
        
        if (data.end) {
          setIsTyping(false);
          if (data.message) {
            setCurrentMessage({ text: data.message, responses: [] });
          }
          
          const dismissDelay = (data.autoAdvance || 2) * 1000;
          setTimeout(() => {
            handleDismiss();
            scheduleReappear(120);
          }, dismissDelay);
          
        } else if (data.show) {
          setCurrentStep(data.step);
          
          setTimeout(() => {
            setIsTyping(false);
            setCurrentMessage({
              text: data.message,
              imageUrl: data.imageUrl,
              responses: data.responses || []
            });
          }, 400);
        } else {
          handleDismiss();
          scheduleReappear(120);
        }
      } else {
        handleDismiss();
      }
    } catch (err) {
      console.error('Failed to respond:', err);
      handleDismiss();
    }
  };

  const scheduleReappear = (seconds) => {
    if (reappearTimerRef.current) clearTimeout(reappearTimerRef.current);
    
    reappearTimerRef.current = setTimeout(() => {
      if (!isVisible && !isSilenced) {
        startBuddyConversation();
      }
    }, seconds * 1000);
  };

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      setCurrentMessage(null);
      setIsExiting(false);
      setIsTyping(false);
      setCurrentStep(null);
      window.__buddyContext = null;
      resetTransport();
    }, 300);
  };

  const handleAvatarClick = () => {
    if (isSilenced) return;
    
    if (isVisible) {
      handleDismiss();
    } else {
      startBuddyConversation();
    }
  };

  // Hide bot if: disabled globally, OR if free users blocked and user is not paid
  if (isLoading || !buddyConfig?.enabled) return null;
  if (!buddyConfig?.freeUserEnabled && !hasPaidPlan) return null;

  const avatarUrl = buddyConfig.avatarUrl;
  const avatarEmoji = buddyConfig.avatar || '🧕';

  return (
    <div className={`fixed ${currentPage === 'decoder' ? 'bottom-32' : 'bottom-20'} right-10 z-50 flex flex-col items-end`}>
      {/* Speech Bubble */}
      {(isVisible || isTyping) && (
        <div className={`mb-3 transition-all duration-300 transform ${
          isExiting ? 'opacity-0 translate-y-4 scale-95' : 'opacity-100 translate-y-0 scale-100'
        }`}>
          <div className="relative max-w-[260px]">
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute -top-2 -right-2 w-6 h-6 bg-gray-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg transition-colors z-10"
            >
              ✕
            </button>

            {/* Main Bubble */}
            <div className={`bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl rounded-br-sm px-4 py-3 shadow-xl ${transportMode ? 'min-w-[280px]' : ''}`}>
              {isTyping ? (
                <div className="flex items-center gap-1 py-1">
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              ) : transportMode ? (
                /* Transport Search UI */
                <div className="space-y-3">
                  <p className="text-white text-[15px] font-medium">{currentMessage?.text || '🚆 Where are you traveling?'}</p>
                  
                  {!transportResult ? (
                    /* City Selection */
                    <>
                      <div className="space-y-2">
                        <select
                          value={fromCity}
                          onChange={(e) => setFromCity(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-white/20 text-white text-sm border border-white/30 focus:outline-none focus:border-white/50"
                        >
                          <option value="" className="text-gray-800">From...</option>
                          {transportCities.map(city => (
                            <option key={city} value={city} className="text-gray-800">{city}</option>
                          ))}
                        </select>
                        <select
                          value={toCity}
                          onChange={(e) => setToCity(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-white/20 text-white text-sm border border-white/30 focus:outline-none focus:border-white/50"
                        >
                          <option value="" className="text-gray-800">To...</option>
                          {transportCities.filter(c => c !== fromCity).map(city => (
                            <option key={city} value={city} className="text-gray-800">{city}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={searchTransport}
                          disabled={!fromCity || !toCity}
                          className="flex-1 px-4 py-2 bg-white text-purple-600 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/90 transition-all"
                        >
                          🔍 Search
                        </button>
                        <button
                          onClick={() => { resetTransport(); handleDismiss(); }}
                          className="px-3 py-2 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30"
                        >
                          ✕
                        </button>
                      </div>
                      <p className="text-yellow-300/70 text-[10px] leading-tight">
                        ⚠️ {buddyConfig?.transportDisclaimer || 'Prices are approximate — verify with station.'}
                      </p>
                    </>
                  ) : (
                    /* Transport Results */
                    <>
                      {/* Transport Type Buttons */}
                      <div className="flex gap-1">
                        {transportResult.transports.map((t, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedTransport(t)}
                            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              selectedTransport?.type === t.type 
                                ? 'bg-white text-purple-600' 
                                : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                          >
                            {t.type === 'train' ? '🚆' : t.type === 'bus' ? '🚌' : '🚕'}
                            {t.is_best && ' ⭐'}
                          </button>
                        ))}
                      </div>
                      
                      {/* Selected Transport Card */}
                      {selectedTransport && (
                        <div className="bg-white/10 rounded-xl p-3 border border-white/20">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">
                              {selectedTransport.type === 'train' ? '🚆' : selectedTransport.type === 'bus' ? '🚌' : '🚕'}
                            </span>
                            <span className="text-white font-medium capitalize">{selectedTransport.type}</span>
                            {selectedTransport.is_best && (
                              <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded-full text-[10px] font-bold">⭐ Best</span>
                            )}
                          </div>
                          
                          <div className="space-y-1 text-white/90 text-sm">
                            <p>⏱ {Math.floor(selectedTransport.time_minutes / 60)}h{selectedTransport.time_minutes % 60 > 0 ? selectedTransport.time_minutes % 60 + 'm' : ''}</p>
                            <div>
                              <p>💰 {selectedTransport.price_min}–{selectedTransport.price_max} MAD</p>
                              <p className="text-white/60 text-xs ml-5">
                                ~€{convertPrice(selectedTransport.price_min).eur}–{convertPrice(selectedTransport.price_max).eur} | ~${convertPrice(selectedTransport.price_min).usd}–{convertPrice(selectedTransport.price_max).usd}
                              </p>
                              <p className="text-yellow-300/70 text-[10px] ml-5 mt-0.5">
                                {buddyConfig?.transportDisclaimer || 'Prices are approximate — verify with station.'}
                              </p>
                            </div>
                            {selectedTransport.frequency_per_day > 0 && (
                              <p>🕒 {formatFrequency(selectedTransport.frequency_per_day)}</p>
                            )}
                            <p className="text-xs text-white/70">
                              📍 {selectedTransport.station_from} → {selectedTransport.station_to}
                            </p>
                            {!selectedTransport.direct && (
                              <p className="text-yellow-300 text-xs">⚠️ Not direct</p>
                            )}
                          </div>
                          
                          {/* Tips */}
                          {selectedTransport.tips?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-white/20">
                              <p className="text-white/80 text-xs">
                                ℹ️ {selectedTransport.tips[0]}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setTransportResult(null); setFromCity(''); setToCity(''); }}
                          className="flex-1 px-3 py-2 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30"
                        >
                          🔄 New Search
                        </button>
                        <button
                          onClick={() => { resetTransport(); handleDismiss(); }}
                          className="px-3 py-2 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30"
                        >
                          ✕ Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : currentMessage && (
                <>
                  {/* Image display if available */}
                  {currentMessage.imageUrl && (
                    <div className="mb-3 rounded-lg overflow-hidden">
                      <img 
                        src={currentMessage.imageUrl} 
                        alt="Bot image" 
                        className="w-full max-h-48 object-cover rounded-lg"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  
                  <p className="text-white text-[15px] leading-relaxed whitespace-pre-line">
                    {currentMessage.text}
                  </p>
                  
                  {currentMessage.textDarija && (
                    <p className="text-white/70 text-sm mt-1 italic">
                      {currentMessage.textDarija}
                    </p>
                  )}

                  {currentMessage.responses && currentMessage.responses.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {currentMessage.responses.map((response, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            if (response.text === "Try another route 🔄") {
                              setTransportResult(null);
                              setFromCity('');
                              setToCity('');
                            } else {
                              handleResponse(response);
                            }
                          }}
                          className="px-3 py-1.5 bg-white/20 hover:bg-white/40 text-white rounded-full text-sm transition-all border border-white/20"
                        >
                          {response.text}
                        </button>
                      ))}
                      {/* Transport Button - show in normal conversation */}
                      {!transportMode && currentStep && (
                        <button
                          onClick={startTransportMode}
                          className="px-3 py-1.5 bg-blue-500/50 hover:bg-blue-500/70 text-white rounded-full text-sm transition-all border border-blue-300/30"
                        >
                          🚆 Travel Help
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Bubble Tail */}
            <div className="absolute -bottom-2 right-4 w-0 h-0 
              border-l-8 border-l-transparent 
              border-r-8 border-r-transparent 
              border-t-[10px] border-t-purple-600">
            </div>
          </div>
        </div>
      )}

      {/* Avatar and Transport Button */}
      <div className="flex items-center gap-2">
        {/* Main Avatar */}
        <div 
          onClick={handleAvatarClick}
          className="relative cursor-pointer"
        >
          {!isVisible && !isSilenced && (
            <div className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-20"></div>
          )}

          <div 
            className={`w-10 h-10 rounded-full shadow-xl overflow-hidden border-2 border-white flex items-center justify-center transition-transform hover:scale-110 ${
              isSilenced ? 'opacity-50' : ''
            }`}
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Buddy" className="w-full h-full object-contain" />
            ) : (
              <span className="text-2xl">{avatarEmoji}</span>
            )}
          </div>
          
          {isSilenced && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-500 rounded-full flex items-center justify-center text-[10px]">
              🔇
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DarijaBuddyBot;
